import { createElement } from 'react';
import type { Filter, FiltersGroup } from '@/app/common/filters/core/filtersTypes.ts';
import type { HandleFilterChanged } from '@/app/common/filters/core/filterComponentType.ts';
import { filterComponentsOf } from '@/app/common/filters/types/filterTypeComponents.ts';
import { filterTypeOf } from '@/app/common/filters/types/filterTypeBehaviour.ts';
import { isFilterSelected } from '@/app/common/filters/core/filterSelectedUtils.ts';
import type { CustomFilterLabelCreators } from '@/app/common/filters/surfaces/forms/FilterGroupsComponents.tsx';
import { selectionLabelForGroup } from '@/app/common/filters/surfaces/labels/selectionLabels.tsx';
import { useFiltersUiData } from '@/app/common/filters/surfaces/provider/filtersUiProvider.tsx';
import { FilterBadgeContent } from '@/app/common/filters/surfaces/badges/FilterBadgeContent.tsx';

const getFilterBadgesFunc = (filter: Filter) => filterComponentsOf(filter).Badge;

type FilterGroupBadgesProps = {
  filtersGroup: FiltersGroup;
  customFilterLabelCreators: CustomFilterLabelCreators;
  isLoading: boolean;
  handleFilterChanged: HandleFilterChanged;
};

export const FilterGroupBadges = ({
  filtersGroup,
  customFilterLabelCreators,
  isLoading,
  handleFilterChanged,
}: FilterGroupBadgesProps) => {
  const { useEvoFilters } = useFiltersUiData();
  const selected = filtersGroup.filters.filter((filter) => isFilterSelected(filter));
  if (!selected.length) return null;

  // Collapsing a group into one badge is an evo behaviour; non-evo keeps a badge per value.
  const selectionLabel = useEvoFilters ? selectionLabelForGroup(filtersGroup) : undefined;

  if (selectionLabel)
    return (
      <FilterBadgeContent
        label={createElement(selectionLabel, filtersGroup)}
        handleFilterClicked={() =>
          handleFilterChanged(selected.map((filter) => filterTypeOf(filter).resetValue(filter)))
        }
      />
    );

  return selected.map((filter) => {
    const FilterFunc = getFilterBadgesFunc(filter);

    return (
      <FilterFunc
        filter={filter}
        customFilterLabelCreator={customFilterLabelCreators[filter.name]}
        isLoading={isLoading}
        handleFilterChanged={handleFilterChanged}
        key={filter.name}
      />
    );
  });
};
